// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useMemo } from 'react'
import { EFFECTEN } from '../../helpers/arbeidsvaardighedenHelpers/arbeidsvaardighedenStandaard'
import useWorkSkillsGrowth from '../../hooks/useWorkSkillsGrowth'
import GroeiPerThema from '../personas/arbeidsvaardigheden/GroeiPerThema'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardCardEmptyState from './DashboardCardEmptyState'

const deelnemersTekst = (aantal) => `${aantal} ${aantal === 1 ? 'deelnemer' : 'deelnemers'}`

/** Datum van de nachtelijke berekening, kort. */
const datumUI = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const dag = String(d.getDate()).padStart(2, '0')
    const maand = String(d.getMonth() + 1).padStart(2, '0')
    return `${dag}-${maand}-${d.getFullYear()}`
}

/**
 * Statische dashboardkaart: de gemiddelde groei op de negen thema's van MSI
 * Arbeidsvaardigheden, over alle deelnemers van de organisatie.
 *
 * De cijfers komen uit een nachtelijk bevroren samenvatting, niet uit een live
 * read. De kaart las eerst de hele WorkSkillsAssessments-collectie bij elke
 * opening van het dashboard — één document per deelnemer per kwartaal, zonder
 * datumgrens, waarvan 44% bestond uit antwoorden die de kaart nooit aanraakt.
 * Nu is het één document. Zelfde patroon als de datakwaliteitskaart.
 *
 * Het getal is een gemiddelde en geen som. Elke deelnemer draagt één groei bij
 * per thema (laatste beoordeling min eerste); een som zou vooral zeggen hoeveel
 * deelnemers er zijn, niet hoeveel ze vooruit zijn gegaan.
 *
 * Er wordt per thema gerekend en nooit over de negen thema's heen. Een
 * gemiddelde over alle thema's kent de standaard niet, en het zou de vijf
 * niet-scorende stellingen via een omweg alsnog laten meewegen. Zie
 * docs/features/arbeidsvaardigheden.md, besluit 5.
 *
 * Beoordelingen van verwijderde deelnemers tellen mee: die groei is echt
 * doorgemaakt (besluit 7). Daarom staat de samenvatting niet op het
 * persona-document — dat wordt op verwijderd gefilterd.
 *
 * De balken hebben geen kleurinstelling: stijging en daling dragen hier de
 * betekenis (groen omhoog, rood omlaag). Een eigen kleur zou dat verschil
 * wegpoetsen, dus deze kaart staat niet in STATIC_CONTROLS_BY_TYPE.
 *
 * @param {object} props
 * @param {{Type: string, docid?: string}} props.item - het DashboardItems-document van deze kaart
 */
const DashboardWorkSkillsGrowthCard = ({ item }) => {
    const { groei, deelnemers, computed, computedAt, loading } = useWorkSkillsGrowth()

    const rijen = useMemo(
        () => EFFECTEN.map((effect) => {
            const cijfer = groei[effect.id]
            return {
                id: effect.id,
                naam: effect.naam,
                groei: Number.isFinite(cijfer?.gemiddelde) ? cijfer.gemiddelde : null,
                meta: cijfer?.deelnemers > 0 ? deelnemersTekst(cijfer.deelnemers) : 'nog niet gemeten',
            }
        }),
        [groei]
    )

    const berekendOp = datumUI(computedAt)

    return (
        <div className='housevisits-card-body'>
            <StaticCardLabel
                title='Groei arbeidsvaardigheden'
                pill={{ variant: 'personas', label: 'Medewerkers' }}
            />

            {loading ? (
                <p className='av-groei-laden'>Laden…</p>
            ) : !computed ? (
                // Nooit berekend is iets anders dan niets gemeten, en die twee zien
                // er op het scherm anders identiek uit. De kaart mag hier geen
                // afwezigheid beweren die zij niet heeft vastgesteld.
                <p className='av-groei-laden'>De groei is voor deze organisatie nog niet berekend. Dat gebeurt elke nacht.</p>
            ) : deelnemers === 0 ? (
                <DashboardCardEmptyState type='work-skills-growth' />
            ) : (
                <GroeiPerThema
                    toelichting={`gemiddelde groei over ${deelnemersTekst(deelnemers)}${berekendOp ? ` · bijgewerkt ${berekendOp}` : ''}`}
                    rijen={rijen}
                    eenheid='punt'
                />
            )}

            <DashboardCardOptions item={item} />
        </div>
    )
}

export default DashboardWorkSkillsGrowthCard
